import type { Entry } from '@/hooks/useSubmissionWorkspace';
import { isWorkspaceTab, resolveWorkspaceTab, resolveWorkspacePins, serializeWorkspacePins } from '@/components/compiler/workspaceTabs';
import { isViewPreset } from '@/components/compiler/workspaceLayout';

// Persisted per-submission view fields. Three of these — `treePinned`,
// `treeVisible`, `treeWidth` — are LEGACY READ-ONLY: the current workspace no
// longer writes them (pins live in `workspacePins`, the tree split in the ratio
// `treeRatio`), but they are still READ so `resolveWorkspacePins` can migrate a
// pre-tab-pins session's open/pinned tree forward (Kimi F5). `treeWidth` was a
// pixel width that is now a durability downgrade to the viewport-relative
// `treeRatio`; it is accepted on read only to not reject the stored blob.
const fields = new Set(['activeMiddleTab', 'selectedSection', 'selectedDocumentId', 'pinnedPanes', 'activePane', 'inspectorTab', 'inspectorTier2', 'statusPipelineExpanded', 'processStatusOpen', 'workspaceTab', 'workspacePins', 'viewPreset', 'treePinned', 'treeVisible', 'treeWidth', 'treeRatio']);
export function readSubmissionWorkspace(key: string): { entries: Entry[]; views: Map<string, unknown> } {
  const empty = { entries: [], views: new Map<string, unknown>() };
  try {
    const data: unknown = JSON.parse(sessionStorage.getItem(key) ?? 'null');
    if (!data || typeof data !== 'object' || !('version' in data) || data.version !== 1 || !('entries' in data) || !Array.isArray(data.entries)) return empty;
    const entries: Entry[] = [];
    for (const item of data.entries) {
      if (!item || typeof item !== 'object' || typeof item.id !== 'string' || !/^[a-zA-Z0-9-]+$/.test(item.id) || typeof item.name !== 'string' || typeof item.url !== 'string' || !(item.sequence === null || typeof item.sequence === 'string')) continue;
      const url = new URL(item.url, window.location.origin);
      const base = `/submissions/${item.id}`;
      if (url.origin !== window.location.origin || !(url.pathname === base || url.pathname.startsWith(`${base}/`)) || entries.some((entry) => entry.id === item.id)) continue;
      entries.push({ id: item.id, name: item.name, sequence: item.sequence, url: url.pathname + url.search + url.hash });
    }
    const views = new Map<string, unknown>();
    // Legacy two-tier selections (inspectorTier1 / inspectorTier2) that map onto
    // a new top-level tab, applied only when no explicit inspectorTab is stored.
    // sessionStorage can carry the old shape across a deploy within one session.
    const legacyTop = new Map<string, string>();
    if ('views' in data && Array.isArray(data.views)) for (const pair of data.views) {
      if (!Array.isArray(pair) || typeof pair[0] !== 'string') continue;
      const [id, field] = pair[0].split(':');
      if (field === 'statusPipelineExpanded' || field === 'processStatusOpen' || field === 'treePinned' || field === 'treeVisible') {
        if (typeof pair[1] !== 'boolean') continue;
      } else if (field === 'treeWidth' || field === 'treeRatio') {
        if (typeof pair[1] !== 'number' || !Number.isFinite(pair[1])) continue;
      } else if (!(pair[1] === null || typeof pair[1] === 'string')) continue;
      // Retired field: a lifecycle tier-1 selection becomes the Lifecycle tab.
      if (field === 'inspectorTier1') { if (pair[1] === 'lifecycle' && id) legacyTop.set(id, 'lifecycle'); continue; }
      if (field === 'inspectorTab' && !['document', 'navigation', 'validation', 'lifecycle', 'operations'].includes(pair[1])) continue;
      if (field === 'inspectorTier2') {
        // The old Links / Navigation sub-tabs are now top-level tabs.
        if (pair[1] === 'navigation') { if (id) legacyTop.set(id, 'navigation'); continue; }
        if (pair[1] === 'links') { if (id) legacyTop.set(id, 'validation'); continue; }
        if (!['placement', 'classification', 'preview', 'compare', 'package', 'annotations'].includes(pair[1])) continue;
      }
      if (field === 'workspaceTab' && !isWorkspaceTab(pair[1])) continue;
      // The view preset governs pane proportions; a hand-edited / unknown value
      // is rejected here so only a valid preset is ever hydrated.
      if (field === 'viewPreset' && !isViewPreset(pair[1])) continue;
      if (field === 'activePane' && !['tree', 'documents', 'inspector'].includes(pair[1])) continue;
      if (field === 'pinnedPanes' && (typeof pair[1] !== 'string' || pair[1].split(',').some((pane) => !['', 'tree', 'documents', 'inspector'].includes(pane)))) continue;
      // The canonical pin set: a comma-joined string of workspace tab keys. An
      // empty string ('' → ['']) is valid and means "nothing pinned".
      if (field === 'workspacePins' && (typeof pair[1] !== 'string' || pair[1].split(',').some((p) => p !== '' && !isWorkspaceTab(p)))) continue;
      if (field && fields.has(field) && entries.some((entry) => entry.id === id)) views.set(pair[0], pair[1]);
    }
    for (const [id, top] of legacyTop) {
      const key = `${id}:inspectorTab`;
      if (!views.has(key) && entries.some((entry) => entry.id === id)) views.set(key, top);
    }
    // Resolve the canonical workspace tab for every submission: an explicit
    // stored workspaceTab wins; otherwise the legacy pane/inspector selection is
    // migrated; otherwise Documents. An unmapped combination never leaves the
    // workspace blank (phase-compiler-tab-pins).
    for (const entry of entries) {
      views.set(`${entry.id}:workspaceTab`, resolveWorkspaceTab(views, entry.id));
    }
    // Resolve the canonical pin set for every submission onto `workspacePins`.
    // An explicit stored `workspacePins` wins (including the empty string =
    // nothing pinned); otherwise the legacy `pinnedPanes` list and the six-tab
    // `treePinned` boolean are migrated onto workspace tab keys; otherwise the
    // default (CTD tree pinned). Never blank — the set always resolves
    // (phase-compiler-tab-pins).
    for (const entry of entries) {
      views.set(`${entry.id}:workspacePins`, serializeWorkspacePins(resolveWorkspacePins(views, entry.id)));
    }
    return { entries, views };
  } catch { return empty; }
}

export function saveSubmissionWorkspace(key: string, entries: Entry[], views: Map<string, unknown>): void {
  try {
    sessionStorage.setItem(key, JSON.stringify({ version: 1, entries: entries.map((entry) => ({ ...entry, sequence: entry.sequence ?? null })), views: [...views].filter(([name]) => fields.has(name.split(':')[1] ?? '')) }));
  } catch { /* Private browsing or full storage: navigation still works in memory. */ }
}
